import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { PerformanceReviewProcessReviewListPageDocument } from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { CreateProcessModal } from 'modules/performance_process/components/review/CreateProcessModal'
import { CreateReviewFromTemplateModal } from 'modules/performance_process/components/review/CreateReviewFromTemplateModal'
import { SelectCreateReviewModal } from 'modules/performance_process/components/review/SelectCreateReviewModal'
import ReviewsDataTable from 'modules/performance_process/components/reviews/ReviewsDataTable'
import { ReviewsEmptyState } from 'modules/performance_process/components/reviews/ReviewsEmptyState'
import performanceWeightsUpsell from 'modules/performance_process/lib/assets/performanceWeightsUpsell.png'
import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

const ReviewsListPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { access, company } = useCurrentSession()
  const { data, loading, error } = useQuery(PerformanceReviewProcessReviewListPageDocument, {
    variables: { companyId: company.id },
  })
  const [showBanner, setShowBanner] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.performanceProcessEnterpriseUpsellingBannerShow,
    true
  )

  const { hasCustomScoreConfigurationEnabled } = useNewPerformanceModuleContext()

  const showUpsellingKit = showBanner && !hasCustomScoreConfigurationEnabled

  const features = keyBy(data?.features?.featuresConnection?.nodes, 'name')
  const hasGoalsConnectionFeature = features['dev_connect_goals_and_reviews_v2']?.enabled

  const cycles = data?.performanceCycles?.cycles ?? []
  const canCreateReview = data?.performance?._policies.createReviewProcessContextless.can
  const canSeePerformanceAnalytics = data?.performance?._policies.seePerformanceAnalytics.can
  const reviewsCount = data?.performance?.reviewProcessesConnection?.totalCount ?? 0

  const showEmptyState = reviewsCount === 0

  const onCloseModal = () => setSearchParams({})

  const handleBannerClose = () => {
    setShowBanner(false)
  }

  return (
    <ResponsivePage.Body showHeader={!showEmptyState} loading={loading} error={error}>
      {access && searchParams.get('modal') === 'select' && (
        <SelectCreateReviewModal onClose={onCloseModal} />
      )}
      {access && searchParams.get('modal') === 'new' && (
        <CreateProcessModal
          authorAccessId={access.id}
          hasCustomScoreConfigurationEnabled={hasCustomScoreConfigurationEnabled}
          hasGoalsConnectionFeature={hasGoalsConnectionFeature}
          onClose={onCloseModal}
          onSubmit={({ id }) =>
            navigate(navigation.performanceProcess.reviews.review.fullPath({ reviewId: id }))
          }
        />
      )}
      {access && searchParams.get('modal') === 'newFromTemplate' && (
        <CreateReviewFromTemplateModal
          authorAccessId={access.id}
          onClose={onCloseModal}
          onSubmit={({ id }) =>
            navigate(navigation.performanceProcess.reviews.review.fullPath({ reviewId: id }))
          }
        />
      )}
      {showEmptyState ? (
        <ReviewsEmptyState canCreateReview={canCreateReview} />
      ) : (
        <Box gap="s16">
          {showUpsellingKit && (
            <UpsellingKit.ProductBanner
              title={i18n.t('performance_process.upsells.weights.title_banner')}
              subtitle={i18n.t('performance_process.upsells.weights.description')}
              mediaUrl={performanceWeightsUpsell}
              requestSource="Performance - weights enterprise plan - reviews list banner"
              moduleRequested={AvailableModules.review_process}
              featureRequested={Features.review_process_custom_score_configuration}
              requestReason={ValidReasons.MissingFeature}
              onClose={handleBannerClose}
              primaryAction={{
                label: i18n.t('upselling_kit.button.more_info'),
                onClick: () => {},
                variant: 'default',
              }}
            />
          )}

          <ReviewsDataTable
            cycles={cycles}
            createProcess={() => setSearchParams({ modal: 'new' })}
            canCreateReview={canCreateReview}
            canSeePerformanceAnalytics={canSeePerformanceAnalytics}
            hasGoalsConnectionFeature={hasGoalsConnectionFeature}
            actions={
              canCreateReview && (
                <>
                  <Button.Router
                    to={navigation.performanceProcess.reviews.templates.fullPath()}
                    type="secondary"
                  >
                    {i18n.t('performance_process.reviews.templates.title')}
                  </Button.Router>
                  <Button.Router
                    iconName="add"
                    to={{
                      pathname: navigation.performanceProcess.reviews.fullPath(),
                      search: createSearchParams({ modal: 'select' }).toString(),
                    }}
                  >
                    {i18n.t('performance_process.reviews.new_review')}
                  </Button.Router>
                </>
              )
            }
          />
        </Box>
      )}
    </ResponsivePage.Body>
  )
}

export default ReviewsListPage
